import { Box, FormLabel, Radio, Stack, Typography } from '@mui/joy';
import { Warning, WarningCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { AllowedDecimalsTooltip, FlexBox, TextLine, Tooltip } from 'core/elements';
import { InputType } from 'core/enums';
import { CostLockTooltip, CostSource } from 'quotes/elements';
import { PriceBasedOn, QuotePartUnifiedStatus } from 'quotes/enums';
import { getPurchaseCostLabel } from 'quotes/utils';
import { Color, Level, Variant } from 'theme/enums';

import { CostValidityFields } from './CostValidityFields';
import { useCostFormFieldsContext } from './state';

interface IProps {
  inline?: boolean;
}

export const PurchaseCostFormFields = ({ inline = false }: IProps) => {
  const { t } = useTranslation();

  const {
    control,
    errors,
    costData,
    costDecimals,
    isAlternateVersionEnabled,
    disablePricingFields,
    isExternalDisabled,
    unifiedPartStatus,
    isCostingCustomValue,
    isNoCostSource,
    costSourceDisplayData,
    selectedSpa,
    priceBasedOn,
    prioritizeLegacyCostValue,
    requiredVendorQuoteNumber,
    standardCostValue,
    formatStandardCost,
    onCostingCustomChange,
    onPrioritizeLegacyCostChange,
    purchaseCostExceedsStandard,
    onBlurPurchaseCost,
    onChangePurchaseCost,
  } = useCostFormFieldsContext();

  const purchaseCostLabel = getPurchaseCostLabel(isAlternateVersionEnabled);

  const inactiveHelperText =
    unifiedPartStatus === QuotePartUnifiedStatus.INACTIVE
      ? [
          <Typography
            key="unifiedPartInactive"
            level={Level.BODY_XS}
            color={Color.WARNING}
            startDecorator={<WarningCircle />}
          >
            {t('labelUnifiedPartInactive')}
          </Typography>,
        ]
      : [];

  const costLocked = !!costData?.costLocked;

  const prioritizeLegacyCostField = isAlternateVersionEnabled && (
    <Tooltip
      title={t(
        prioritizeLegacyCostValue
          ? 'messagePrioritizeLegacyCostOn'
          : 'messagePrioritizeLegacyCostOff'
      )}
      color={Color.PRIMARY}
      placement="top"
    >
      <Box>
        <FormField
          name="prioritizeLegacyCost"
          type={InputType.CHECKBOX}
          inputProps={{
            label: t('labelPrioritizeLegacyCost'),
            control,
            onChange: onPrioritizeLegacyCostChange,
            disabled:
              priceBasedOn === PriceBasedOn.REPLACEMENT || disablePricingFields || costLocked,
          }}
        />
      </Box>
    </Tooltip>
  );

  const inlineFields = (
    <Stack gap={2}>
      <Typography level={Level.TITLE_MD}>{purchaseCostLabel}</Typography>

      <FormField
        name="isCostingCustom"
        type={InputType.SWITCH}
        inputProps={{
          label: t(
            isAlternateVersionEnabled ? 'labelOverwriteQuoteCost' : 'labelOverwritePurchaseCost'
          ),
          control,
          error: errors?.isCostingCustom,
          onChange: onCostingCustomChange,
          disabled: disablePricingFields || costLocked,
          variant: disablePricingFields || costLocked ? Variant.OUTLINED : Variant.SOLID,
          disabledTooltip: costLocked ? t('messageCostLockedByContract') : null,
        }}
      />

      <FormField
        name="purchaseCost"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          control,
          error: errors?.purchaseCost,
          label: (
            <FlexBox>
              {purchaseCostLabel}
              <CostLockTooltip costLocked={costData?.costLocked} />
            </FlexBox>
          ),
          placeholder: purchaseCostLabel,
          color: errors?.purchaseCost
            ? Color.DANGER
            : purchaseCostExceedsStandard
            ? Color.WARNING
            : undefined,
          startDecorator: purchaseCostExceedsStandard ? <Warning weight="fill" /> : null,
          onChange: onChangePurchaseCost,
          onBlur: onBlurPurchaseCost,
          disabled: !isCostingCustomValue || disablePricingFields,
          endDecorator: <AllowedDecimalsTooltip decimals={costDecimals} />,
          slotProps: {
            input: {
              prefix: '$',
              thousandSeparator: true,
              decimalScale: costDecimals,
              fixedDecimalScale: true,
            },
          },
          helperTexts: [
            ...(isAlternateVersionEnabled ? inactiveHelperText : []),
            ...(purchaseCostExceedsStandard
              ? [
                  <Typography key="costExceedsStandard" level={Level.BODY_XS} color={Color.WARNING}>
                    {t('messagePurchaseCostExceedsStandardCost')}
                  </Typography>,
                ]
              : []),
          ],
        }}
      />

      {selectedSpa ? (
        // Display only, bound to no form field: the pick's save posts a null vendor quote number.
        <FormField
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelReplacementCostNumber'),
            placeholder: t('labelReplacementCostNumber'),
            value: selectedSpa.spaCostRegistrationNum ?? '',
            disabled: true,
          }}
        />
      ) : (
        <FormField
          name="costSource.vendorQuoteNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelVendorQuoteNumber'),
            placeholder: t('labelVendorQuoteNumber'),
            control,
            error: errors?.costSource?.vendorQuoteNumber,
            // Deliberately NOT disabled while the prices call runs (no isCalculating/
            // disablePricingFields): the number is user-entered and typed mid-call; the response
            // writes it only while the Overwrite toggle is off (see processCostResponse).
            disabled: !isCostingCustomValue || isExternalDisabled,
            required: requiredVendorQuoteNumber,
          }}
        />
      )}

      <CostValidityFields />

      <Box>
        <FormLabel>{t('labelCalculatedStandardCost')}</FormLabel>
        <Typography level={Level.TITLE_MD} sx={{ marginTop: 1 }}>
          {formatStandardCost(standardCostValue)}
        </Typography>
      </Box>

      <Box>
        <FormLabel>{t('labelCostSource')}</FormLabel>
        {isNoCostSource ? (
          <TextLine text={t('messageNoCost')} bold />
        ) : (
          <CostSource costSource={costSourceDisplayData} />
        )}
      </Box>
    </Stack>
  );

  if (inline) {
    return (
      <Stack gap={1}>
        {inlineFields}
        {prioritizeLegacyCostField}
      </Stack>
    );
  }

  return (
    <Stack sx={{ gap: 2, height: '100%', justifyContent: 'space-between' }}>
      {inlineFields}

      <Stack gap={1}>
        <Radio
          value={PriceBasedOn.QUOTED}
          label={t('labelUseQuotedCostToCalculatePrice')}
          disabled={disablePricingFields}
        />

        {prioritizeLegacyCostField}
      </Stack>
    </Stack>
  );
};
